// PickupEvaluateDialog.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
    Button,
    Container,
    MenuItem,
    Select,
    Stack,
    Typography,
    CircularProgress,
    TextField,
    InputAdornment,
    Alert,
    AlertTitle,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    FormControl,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EChartsReact from "echarts-for-react";
import Axios from "axios";

import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import utc from "dayjs/plugin/utc";
import { DEFAULT_HOTEL, SanitizeHotelID, URLAPI } from "../configuration";
import { HORIZONS } from "../pages/PickupScreen";
import { HotelSelector } from "./TopBar";

const SMOOTH_WINDOW = 5;
const PickupEvaluateDialog = ({
    open,
    onClose,
    authToken,
    hotelId,
    availableModelsMap,
    defaultModelName,
}) => {
    const [evaluateAsOf, setEvaluateAsOf] = useState(
        dayjs.utc().format("YYYY-MM-DD"),
    );
    const [evaluateSelectedHorizons, setEvaluateSelectedHorizons] =
        useState(HORIZONS);
    const [evaluateSelectedModels, setEvaluateSelectedModels] = useState([]);
    const [evaluateIsLoading, setEvaluateIsLoading] = useState(false);
    const [evaluateError, setEvaluateError] = useState(null);
    const [evaluateResult, setEvaluateResult] = useState(null);
    const [evaluateCache, setEvaluateCache] = useState({});
    const [evaluateHistory, setEvaluateHistory] = useState([]);
    const [evaluateActiveHorizon, setEvaluateActiveHorizon] = useState(null);
    const [evaluateActiveModel, setEvaluateActiveModel] = useState(null);
    const [evaluateBlendHorizons, setEvaluateBlendHorizons] = useState(true);

    const [selectedHotel, setSelectedHotel] = useState(
        hotelId || DEFAULT_HOTEL,
    );
    // Normalize hotel key in the same way as the main screen
    const hotelKey = useMemo(() => {
        const raw = SanitizeHotelID(selectedHotel);
        return String(raw ?? "").trim();
    }, [selectedHotel]);

    const evaluateModelKeys = useMemo(() => {
        const keys = Object.keys(availableModelsMap || {});
        return keys
            .map((k) => String(k).replace(".joblib", "").replace(".pkl", ""))
            .sort();
    }, [availableModelsMap]);

    const evaluateActiveMetrics = useMemo(() => {
        if (
            !evaluateResult ||
            evaluateActiveHorizon == null ||
            !evaluateActiveModel
        )
            return null;

        const hKey = String(evaluateActiveHorizon);
        const hData = evaluateResult.by_horizon?.[hKey];
        if (!hData || !Array.isArray(hData.metrics)) return null;

        const row = hData.metrics.find(
            (m) =>
                String(m.model) === String(evaluateActiveModel) &&
                Number(m.horizon) === Number(evaluateActiveHorizon),
        );
        return row || null;
    }, [evaluateResult, evaluateActiveHorizon, evaluateActiveModel]);

    // File: PickupEvaluateDialog.jsx

    // PickupEvaluateDialog.jsx

    const evaluateChartOption = useMemo(() => {
        if (!evaluateResult || evaluateActiveHorizon == null) return null;

        const activeH = Number(evaluateActiveHorizon);
        if (!Number.isFinite(activeH)) return null;

        const horizonsList =
            Array.isArray(evaluateResult.horizons) &&
            evaluateResult.horizons.length > 0
                ? evaluateResult.horizons
                      .map((h) => Number(h))
                      .filter((h) => Number.isFinite(h))
                      .sort((a, b) => a - b)
                : HORIZONS;

        if (!horizonsList || horizonsList.length === 0) return null;

        // Horizon usado para el eje X:
        // - si blend está activado, usamos el mayor (normalmente 60d) para tener toda la ventana
        // - si no, usamos el horizonte activo, como antes
        const axisH = evaluateBlendHorizons
            ? horizonsList[horizonsList.length - 1]
            : activeH;
        // const axisH = horizonsList[horizonsList.length - 1];

        const getDailyForHorizon = (h) => {
            const hKey = String(h);
            const hData = evaluateResult.by_horizon?.[hKey];
            if (!hData || !Array.isArray(hData.daily_table)) return [];
            return hData.daily_table;
        };

        const dailyAxis = getDailyForHorizon(axisH);
        if (!dailyAxis || dailyAxis.length === 0) return null;

        const asOfUtc = dayjs.utc(
            evaluateAsOf || dayjs.utc().format("YYYY-MM-DD"),
        );

        // Indexamos daily_table por fecha para cada horizonte disponible
        const dailyByHorizon = {};
        horizonsList.forEach((h) => {
            const rows = getDailyForHorizon(h);
            if (!rows || rows.length === 0) return;
            const index = {};
            rows.forEach((row) => {
                const checkIn = row.CheckIn || row.checkin || row.check_in;
                if (!checkIn) return;
                const dateKey = dayjs.utc(checkIn).format("YYYY-MM-DD");
                index[dateKey] = row;
            });
            dailyByHorizon[h] = index;
        });

        const chooseHForBlend = (idx) => {
            if (idx < 0) return null;
            // mismo esquema que en PickupScreen: 0-6 → 7d, 7-13 → 14d, 14-29 → 30d, resto → 60d
            const tier = idx < 7 ? 7 : idx < 14 ? 14 : idx < 30 ? 30 : 60;
            let hNum = activeH;
            let candidate = Math.min(tier, hNum);

            // aseguramos que el horizonte elegido existe en los datos
            const available = horizonsList.filter((h) => h <= candidate);
            if (available.length === 0) return null;
            candidate = available[available.length - 1];
            return candidate;
        };

        // Build the effective daily rows that the chart will use
        // When blend is ON: real + predictions are horizon-blended
        // When blend is OFF: use only the selected horizon
        let dailyForChart;

        if (evaluateBlendHorizons) {
            dailyForChart = dailyAxis.map((axisRow) => {
                const checkIn =
                    axisRow.CheckIn || axisRow.checkin || axisRow.check_in;
                const dateKey = dayjs.utc(checkIn).format("YYYY-MM-DD");
                const idx = dayjs.utc(checkIn).diff(asOfUtc, "day");

                const useH = chooseHForBlend(idx);

                if (!useH || !dailyByHorizon[useH]) {
                    return axisRow;
                }

                const rowFromH = dailyByHorizon[useH][dateKey] ?? axisRow;
                return rowFromH;
            });
        } else {
            // No blend: the chart uses only the active horizon
            dailyForChart = getDailyForHorizon(activeH);
        }

        const xLabels = dailyForChart.map((row) =>
            dayjs
                .utc(row.CheckIn || row.checkin || row.check_in)
                .format("DD MMM"),
        );
        const pickupReal = dailyForChart.map((row) =>
            Number(row.Pickup_H ?? row.pickup_h ?? 0),
        );
        const otbT0 = dailyForChart.map((row) => Number(row.OTB_t0 ?? 0));
        const otbTH = dailyForChart.map((row) => Number(row.OTB_tH ?? 0));

        const movingAverage = (values, windowSize) => {
            if (!Array.isArray(values) || values.length === 0) return [];
            const result = [];
            const half = Math.floor(windowSize / 2);

            for (let i = 0; i < values.length; i++) {
                let from = Math.max(0, i - half);
                let to = Math.min(values.length - 1, i + half);
                let sum = 0;
                let count = 0;

                for (let j = from; j <= to; j++) {
                    sum += values[j];
                    count += 1;
                }

                result.push(count > 0 ? sum / count : 0);
            }

            return result;
        };

        const pickupRealSmooth = movingAverage(pickupReal, SMOOTH_WINDOW);

        const modelsList =
            Array.isArray(evaluateResult.models) &&
            evaluateResult.models.length > 0
                ? evaluateResult.models
                : evaluateSelectedModels;

        // Legend selection map: smooth y OTB apagados por defecto
        const legendSelected = {
            "Pickup (real)": true,
            "Pickup (real, smooth)": false,
            "OTB as_of": false,
            "OTB t+H": false,
        };

        // Series base
        const baseSeries = [
            {
                name: "Pickup (real)",
                type: "line",
                data: pickupReal,
                smooth: true,
            },
        ];

        const smoothSeries = [
            {
                name: "Pickup (real, smooth)",
                type: "line",
                data: pickupRealSmooth,
                smooth: true,
                symbol: "none",
            },
        ];

        (modelsList || []).forEach((modelNameForChart) => {
            const pickupPredKey = `Pickup_hat_${modelNameForChart}`;
            const pickupPred = dailyForChart.map((row) =>
                Number(row[pickupPredKey] ?? 0),
            );
            const pickupPredSmooth = movingAverage(pickupPred, SMOOTH_WINDOW);

            const predName = `Pickup predicted (${modelNameForChart})`;
            const predSmoothName = `${predName} (smooth)`;

            baseSeries.push({
                name: predName,
                type: "line",
                data: pickupPred,
                smooth: true,
            });

            smoothSeries.push({
                name: predSmoothName,
                type: "line",
                data: pickupPredSmooth,
                smooth: true,
                symbol: "none",
            });

            // Predicción base encendida, smoothed apagada
            legendSelected[predName] = true;
            legendSelected[predSmoothName] = false;
        });

        const otbSeries = [
            {
                name: "OTB as_of",
                type: "line",
                data: otbT0,
                smooth: true,
            },
            {
                name: "OTB t+H",
                type: "line",
                data: otbTH,
                smooth: true,
            },
        ];

        // Orden final: base, OTB, smooth al final
        const series = [...baseSeries, ...otbSeries, ...smoothSeries];

        return {
            grid: { left: 40, right: 20, top: 60, bottom: 40 },
            tooltip: { trigger: "axis" },
            legend: {
                top: 0,
                selected: legendSelected,
            },
            xAxis: {
                type: "category",
                data: xLabels,
                axisTick: { alignWithLabel: true },
            },
            yAxis: { type: "value" },
            series,
        };
    }, [
        evaluateResult,
        evaluateActiveHorizon,
        evaluateSelectedModels,
        evaluateAsOf,
        evaluateBlendHorizons,
    ]);

    const handleRunEvaluate = async () => {
        try {
            setEvaluateIsLoading(true);
            setEvaluateError(null);

            const as_of = evaluateAsOf || dayjs.utc().format("YYYY-MM-DD");

            const horizonsArr = HORIZONS;

            const modelsArr =
                evaluateSelectedModels && evaluateSelectedModels.length > 0
                    ? evaluateSelectedModels
                    : [];

            if (modelsArr.length === 0) {
                setEvaluateError("Please select at least one model.");
                return;
            }

            const hSorted = horizonsArr
                .map((h) => Number(h))
                .filter((h) => Number.isFinite(h))
                .sort((a, b) => a - b);
            const mSorted = modelsArr
                .map((m) => String(m).trim())
                .filter(Boolean)
                .sort();

            const modelsStr = mSorted.join(",");

            const cacheKey = `${hotelKey}|${as_of}|${modelsStr}`;
            const cached = evaluateCache[cacheKey];
            if (cached) {
                setEvaluateResult(cached);
                const hList = Array.isArray(cached.horizons)
                    ? cached.horizons
                    : hSorted;
                const mList = Array.isArray(cached.models)
                    ? cached.models
                    : mSorted;

                const defaultHorizon =
                    hList && hList.length > 0
                        ? (hList.find((h) => Number(h) === 30) ?? hList[0])
                        : null;

                setEvaluateActiveHorizon(defaultHorizon);
                setEvaluateActiveModel(
                    mList && mList.length > 0 ? mList[0] : null,
                );
                setEvaluateHistory((prev) => {
                    const next = prev.filter((it) => it.key !== cacheKey);
                    return [
                        {
                            key: cacheKey,
                            as_of,
                            horizons: hList,
                            models: mList,
                            hotel: hotelKey,
                            createdAt: dayjs.utc().toISOString(),
                        },
                        ...next,
                    ].slice(0, 5);
                });
                return;
            }

            const params = {
                as_of,
                horizons: "7,14,30,60",
                models: modelsStr,
                hotel_id: hotelKey,
                plot: 0,
            };

            const resp = await Axios.get(
                `${URLAPI}/AI/predict/pickup/evaluate`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                    params,
                },
            );
            console.log("Evaluate response:", resp);

            const payload = resp?.data?.data ?? resp?.data ?? null;
            if (!payload) {
                throw new Error("Empty response");
            }

            setEvaluateCache((prev) => ({
                ...prev,
                [cacheKey]: payload,
            }));

            setEvaluateResult(payload);

            const hList = Array.isArray(payload.horizons)
                ? payload.horizons
                : hSorted;
            const mList = Array.isArray(payload.models)
                ? payload.models
                : mSorted;

            const defaultHorizon =
                hList && hList.length > 0
                    ? (hList.find((h) => Number(h) === 30) ?? hList[0])
                    : null;

            setEvaluateActiveHorizon(defaultHorizon);
            setEvaluateActiveModel(mList && mList.length > 0 ? mList[0] : null);

            setEvaluateHistory((prev) => {
                const next = prev.filter((it) => it.key !== cacheKey);
                return [
                    {
                        key: cacheKey,
                        as_of,
                        horizons: hList,
                        models: mList,
                        hotel: hotelKey,
                        createdAt: dayjs.utc().toISOString(),
                    },
                    ...next,
                ].slice(0, 5);
            });
        } catch (e) {
            console.error("Evaluate request failed:", e);
            const apiMsg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                "Unknown error";
            const status = e?.response?.status
                ? ` (HTTP ${e.response.status})`
                : "";
            setEvaluateError(
                `Could not load evaluation data${status}: ${apiMsg}`,
            );
        } finally {
            setEvaluateIsLoading(false);
        }
    };

    const handleLoadEvaluationFromHistory = (entry) => {
        if (!entry || !entry.key) return;
        const cached = evaluateCache[entry.key];
        if (!cached) return;

        setEvaluateAsOf(entry.as_of || evaluateAsOf);
        setEvaluateSelectedHorizons(entry.horizons || HORIZONS);
        setEvaluateSelectedModels(entry.models || []);
        setEvaluateResult(cached);

        const hList = Array.isArray(cached.horizons)
            ? cached.horizons
            : entry.horizons || [];
        const mList = Array.isArray(cached.models)
            ? cached.models
            : entry.models || [];

        const defaultHorizon =
            hList && hList.length > 0
                ? (hList.find((h) => Number(h) === 30) ?? hList[0])
                : null;

        setEvaluateActiveHorizon(defaultHorizon);
        setEvaluateActiveModel(mList && mList.length > 0 ? mList[0] : null);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                Evaluate models
                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                    aria-label="close"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {/* Controls row */}
                <Stack
                    direction="row"
                    spacing={3}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ alignItems: "flex-end" }}
                >
                    <Stack>
                        <Typography variant="subtitle2">As of date</Typography>
                        <TextField
                            type="date"
                            size="small"
                            value={evaluateAsOf}
                            onChange={(e) => setEvaluateAsOf(e.target.value)}
                            inputProps={{
                                max: dayjs.utc().format("YYYY-MM-DD"),
                            }}
                        />
                    </Stack>

                    <Stack>
                        <Typography variant="subtitle2">Models</Typography>
                        <Select
                            multiple
                            size="small"
                            value={evaluateSelectedModels}
                            onChange={(e) =>
                                setEvaluateSelectedModels(e.target.value)
                            }
                            sx={{ minWidth: 220 }}
                            renderValue={(selected) =>
                                (selected || []).slice().sort().join(", ")
                            }
                            disabled={evaluateModelKeys.length === 0}
                        >
                            {evaluateModelKeys.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>
                    <FormControl>
                        <Typography variant="subtitle2">Hotel</Typography>
                        {/* Controlled HotelSelector */}
                        <HotelSelector
                            value={selectedHotel}
                            onChange={(newHotelId) => {
                                // Keep local state in sync with selector
                                setSelectedHotel(newHotelId);
                            }}
                            imgMaxWidth={100}
                        />
                    </FormControl>

                    <Button
                        variant="contained"
                        onClick={handleRunEvaluate}
                        disabled={evaluateIsLoading}
                        startIcon={
                            evaluateIsLoading ? (
                                <CircularProgress size={16} />
                            ) : null
                        }
                    >
                        {evaluateIsLoading ? "Evaluating…" : "Run evaluation"}
                    </Button>
                </Stack>

                {evaluateError && (
                    <Alert
                        severity="error"
                        sx={{ mt: 2 }}
                        onClose={() => setEvaluateError(null)}
                    >
                        <AlertTitle>Evaluation failed</AlertTitle>
                        {evaluateError}
                    </Alert>
                )}

                {evaluateHistory.length > 0 && (
                    <Stack sx={{ mt: 2 }} spacing={1}>
                        <Typography variant="subtitle2">
                            Recent evaluations
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {evaluateHistory.map((entry) => (
                                <Chip
                                    key={entry.key}
                                    variant="outlined"
                                    label={`${
                                        entry.as_of
                                    } | ${entry.models.join(",")} | ${
                                        entry.hotel ?? "?"
                                    }`}
                                    onClick={() =>
                                        handleLoadEvaluationFromHistory(entry)
                                    }
                                    size="small"
                                />
                            ))}
                        </Stack>
                    </Stack>
                )}

                {evaluateResult && (
                    <>
                        {/* Horizon / model selectors for the chart */}
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{ mt: 3 }}
                            useFlexGap
                            flexWrap="wrap"
                        >
                            <Stack>
                                <Typography variant="subtitle2">
                                    Horizon
                                </Typography>
                                <Select
                                    size="small"
                                    value={
                                        evaluateActiveHorizon ??
                                        evaluateResult.horizons?.[0] ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        setEvaluateActiveHorizon(
                                            Number(e.target.value),
                                        )
                                    }
                                    sx={{ minWidth: 120 }}
                                >
                                    {(evaluateResult.horizons || []).map(
                                        (h) => (
                                            <MenuItem key={h} value={h}>
                                                {h}d
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </Stack>

                            <Stack>
                                <Typography variant="subtitle2">
                                    Model
                                </Typography>
                                <Select
                                    size="small"
                                    value={
                                        evaluateActiveModel ??
                                        evaluateResult.models?.[0] ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        setEvaluateActiveModel(e.target.value)
                                    }
                                    sx={{ minWidth: 180 }}
                                >
                                    {(evaluateResult.models || []).map((m) => (
                                        <MenuItem key={m} value={m}>
                                            {m}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                            <Stack>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={evaluateBlendHorizons}
                                            onChange={(e) =>
                                                setEvaluateBlendHorizons(
                                                    e.target.checked,
                                                )
                                            }
                                            size="small"
                                        />
                                    }
                                    label="Blend horizons (7/14/30/60)"
                                />
                            </Stack>
                        </Stack>

                        {/* Metrics summary for the active (horizon, model) */}
                        {evaluateActiveMetrics && (
                            <Stack
                                direction="row"
                                spacing={3}
                                sx={{ mt: 2 }}
                                flexWrap="wrap"
                                useFlexGap
                            >
                                <Typography variant="body2">
                                    <b>n</b>: {evaluateActiveMetrics.n ?? "-"}
                                </Typography>
                                <Typography variant="body2">
                                    <b>MAE</b>:{" "}
                                    {evaluateActiveMetrics.mae != null
                                        ? evaluateActiveMetrics.mae.toFixed(2)
                                        : "-"}
                                </Typography>
                                <Typography variant="body2">
                                    <b>RMSE</b>:{" "}
                                    {evaluateActiveMetrics.rmse != null
                                        ? evaluateActiveMetrics.rmse.toFixed(2)
                                        : "-"}
                                </Typography>
                                <Typography variant="body2">
                                    <b>MAPE</b>:{" "}
                                    {evaluateActiveMetrics.mape != null
                                        ? `${evaluateActiveMetrics.mape.toFixed(
                                              1,
                                          )}%`
                                        : "-"}
                                </Typography>
                                <Typography variant="body2">
                                    <b>Bias</b>:{" "}
                                    {evaluateActiveMetrics.bias != null
                                        ? evaluateActiveMetrics.bias.toFixed(2)
                                        : "-"}
                                </Typography>
                                <Typography variant="body2">
                                    <b>Window days</b>:{" "}
                                    {evaluateActiveMetrics.trained_window_days ??
                                        "-"}
                                    {" / "}
                                    {evaluateActiveMetrics.used_days_forward ??
                                        "-"}
                                </Typography>
                            </Stack>
                        )}

                        {/* Chart with real vs predicted pickup and OTB curves */}
                        {evaluateChartOption && (
                            <div
                                style={{
                                    height: 280,
                                    marginTop: 16,
                                }}
                            >
                                <EChartsReact
                                    style={{ height: "100%" }}
                                    option={evaluateChartOption}
                                />
                            </div>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PickupEvaluateDialog;
